// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type {
  FactFindQuestion,
  FactFindSchema,
  WizardGate,
  WizardGroup,
  WizardSection,
} from '../../api/profileFormApi';

/**
 * Pure builders turning a DSL v2 (sectioned wizard) version's answers into a
 * read-only display tree for the advisor's Fact Find tab. Kept free of React
 * so the formatting rules are directly unit-testable. DSL v1 (flat) versions
 * keep the legacy rendering path in ProfileFormTab.
 */

export type AnswerMap = Record<string, unknown>;

export interface DisplayRow {
  label: string;
  value: string;
}

export interface DisplayBlock {
  /** Sub-heading (group title / per-client accordion title); empty for plain rows. */
  title?: string;
  rows: DisplayRow[];
  /** Repeating-group entries — one row list per item. */
  items?: DisplayRow[][];
}

export interface SectionView {
  id: string;
  title: string;
  blocks: DisplayBlock[];
}

/** A schema is the sectioned wizard DSL when it carries full section objects. */
export function isSectionedSchema(schema: FactFindSchema | null | undefined): boolean {
  if (!schema) return false;
  if (schema.dslVersion === 2) return true;
  return !schema.questions && Array.isArray(schema.sections) && schema.sections.length > 0;
}

const FREQUENCY_LABELS: Record<string, string> = {
  ANNUALLY: 'Annually',
  MONTHLY: 'Monthly',
  WEEKLY: 'Weekly',
  QUARTERLY: 'Quarterly',
  ONCE_OFF: 'Once off',
};

function isMap(v: unknown): v is AnswerMap {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function optionLabel(field: FactFindQuestion | undefined, value: unknown): string {
  const opt = (field?.options ?? []).find(
    (o) => (typeof o === 'string' ? o : o.value) === String(value),
  );
  if (opt && typeof opt === 'object') return opt.label ?? opt.value;
  return String(value);
}

/** Human formatting for one answered v2 value. Exported for unit tests. */
export function formatValue(
  field: FactFindQuestion | undefined,
  value: unknown,
  clientNames: Record<string, string>,
): string {
  if (value == null) return '';
  switch (field?.type) {
    case 'boolean':
      return String(value) === 'true' ? 'Yes' : 'No';
    case 'enum':
      return optionLabel(field, value);
    case 'percent':
      return `${value}%`;
    case 'money': {
      if (!isMap(value)) return String(value);
      const amount = value.amount == null ? '' : Number(value.amount).toLocaleString();
      const currency = (value.currency as string) ?? 'EUR';
      const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€';
      const frequency = value.frequency
        ? ` ${FREQUENCY_LABELS[value.frequency as string] ?? value.frequency}`
        : '';
      return `${symbol}${amount}${frequency}`;
    }
    case 'term': {
      if (!isMap(value)) return String(value);
      const unit = value.unit === 'YEARS' ? 'years' : 'months';
      return `${value.value} ${unit}`;
    }
    case 'phone': {
      if (isMap(value)) {
        return [value.countryCode, value.number].filter(Boolean).join(' ');
      }
      return String(value);
    }
    case 'address':
      return isMap(value) ? String(value.text ?? '') : String(value);
    case 'clientRef':
      if (value === 'JOINT') return 'Joint';
      return clientNames[String(value)] ?? 'Client';
    case 'document':
      return 'Document uploaded';
    default:
      if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
      return String(value);
  }
}

function gateRow(gate: WizardGate, answers: AnswerMap): DisplayRow | null {
  const value = answers[gate.id];
  if (value == null) return null;
  if (gate.type === 'boolean') {
    return { label: gate.label, value: value === true ? 'Yes' : 'No' };
  }
  if (!Array.isArray(value) || value.length === 0) return null;
  const labels = value.map((v) => {
    const opt = (gate.options ?? []).find((o) => o.value === String(v));
    return opt?.label ?? String(v);
  });
  return { label: gate.label, value: labels.join(', ') };
}

function fieldRows(
  fields: FactFindQuestion[],
  answers: AnswerMap,
  clientNames: Record<string, string>,
): DisplayRow[] {
  const rows: DisplayRow[] = [];
  for (const field of fields) {
    const value = answers[field.id];
    if (value == null || value === '') continue;
    rows.push({ label: field.label ?? field.id, value: formatValue(field, value, clientNames) });
  }
  return rows;
}

function groupBlock(
  group: WizardGroup,
  sectionAnswers: AnswerMap,
  clientNames: Record<string, string>,
): DisplayBlock | null {
  const rows: DisplayRow[] = [];
  if (group.gate) {
    const row = gateRow(group.gate, sectionAnswers);
    if (row) rows.push(row);
  }
  const value = sectionAnswers[group.id];
  if (group.repeating && Array.isArray(value)) {
    const items = value
      .filter(isMap)
      .map((item) => fieldRows(group.fields, item, clientNames))
      .filter((itemRows) => itemRows.length > 0);
    if (rows.length === 0 && items.length === 0) return null;
    return { title: group.title, rows, items };
  }
  if (!group.repeating && isMap(value)) {
    rows.push(...fieldRows(group.fields, value, clientNames));
  }
  if (rows.length === 0) return null;
  return { title: group.title, rows };
}

function sharedSectionBlocks(
  section: WizardSection,
  answers: AnswerMap,
  clientNames: Record<string, string>,
): DisplayBlock[] {
  const blocks: DisplayBlock[] = [];
  const headRows: DisplayRow[] = [];
  if (section.gate) {
    const row = gateRow(section.gate, answers);
    if (row) headRows.push(row);
  }
  headRows.push(...fieldRows(section.fields ?? [], answers, clientNames));
  if (headRows.length > 0) {
    blocks.push({ rows: headRows });
  }
  for (const group of section.groups ?? []) {
    const block = groupBlock(group, answers, clientNames);
    if (block) blocks.push(block);
  }
  return blocks;
}

function perClientBlocks(
  section: WizardSection,
  answers: AnswerMap,
  clientNames: Record<string, string>,
): DisplayBlock[] {
  const byClient = answers.byClient;
  if (!isMap(byClient)) return [];
  const blocks: DisplayBlock[] = [];
  for (const [clientId, clientAnswers] of Object.entries(byClient)) {
    if (!isMap(clientAnswers)) continue;
    const rows = fieldRows(section.fields ?? [], clientAnswers, clientNames);
    if (rows.length > 0) {
      blocks.push({ title: clientNames[clientId] ?? 'Client', rows });
    }
  }
  return blocks;
}

function chatBlocks(answers: AnswerMap): DisplayBlock[] {
  const chatAnswers = answers.answers;
  if (!isMap(chatAnswers)) return [];
  const rows = Object.entries(chatAnswers)
    .filter(([, v]) => v != null && v !== '')
    .map(([key, v]) => ({ label: key, value: String(v) }));
  return rows.length > 0 ? [{ rows }] : [];
}

/**
 * The full display tree for a sectioned version: one SectionView per schema
 * section the client answered (empty sections are omitted).
 */
export function buildSectionViews(
  schema: FactFindSchema,
  answers: AnswerMap,
  clientNames: Record<string, string>,
): SectionView[] {
  const views: SectionView[] = [];
  for (const section of schema.sections ?? []) {
    const sectionAnswers = answers[section.id];
    if (!isMap(sectionAnswers)) continue;
    let blocks: DisplayBlock[];
    if (section.kind === 'CHAT') {
      blocks = chatBlocks(sectionAnswers);
    } else if (section.layout === 'PER_CLIENT') {
      blocks = perClientBlocks(section, sectionAnswers, clientNames);
    } else {
      blocks = sharedSectionBlocks(section, sectionAnswers, clientNames);
    }
    if (blocks.length > 0) {
      views.push({ id: section.id, title: section.title, blocks });
    }
  }
  return views;
}
